import { combineReducers } from 'redux';
import * as ActionTypes from 'actions/CartActions';
import * as PaymentSettingsActionTypes from 'payments/actions/SettingsActions';
import finalizePurchase from 'payments/reducers/FinalizePurchaseReducers';
import shipping, * as fromShipping from './shipping';
import items, * as fromItems from './items';
import outOfStockItems, * as fromOutOfStockItems from './outOfStockItems';
import isProcessingCart from './isProcessingCart';
import root from 'window-or-global';

const initialOtherCartProps = {
  amount: 0,
  isShippingRequired: false,
  shippingCharges: 0,
  claimUrl: '',
};
const otherCartProps = (state = initialOtherCartProps, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_FETCH_CART_SUCCESS:
    case ActionTypes.HANDLE_ADD_TO_CART_SUCCESS:
    case ActionTypes.HANDLE_REMOVE_ITEM_FROM_CART_SUCCESS:
    case ActionTypes.HANDLE_EDIT_CART_ITEM_QUANTITY_SUCCESS:
    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
    case ActionTypes.HANDLE_CLEAR_FAULTY_CART_SUCCESS:
      try {
        const { totalAmount, isShippingRequired, shippingCharges } = action.response.cart;
        return {
          amount: Number(totalAmount),
          isShippingRequired,
          shippingCharges,
        };
      } catch (e) {
        return initialOtherCartProps;
      }

    case ActionTypes.HANDLE_EDIT_CART_ITEM_QUANTITY_FAILURE: {
      const { isShippingRequired, totalAmount, shippingCharges } = action.errors.cart;
      return {
        amount: Number(totalAmount),
        isShippingRequired,
        shippingCharges,
      };
    }

    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS:
      const { claimUrl } = action.response.details;
      if (claimUrl) {
        return {
          ...state,
          claimUrl,
        };
      }
      return state;

    default:
      return state;
  }
};

const isFetching = (state = true, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_FETCH_CART_REQUEST:
      return true;
    case ActionTypes.HANDLE_FETCH_CART_SUCCESS:
    case ActionTypes.HANDLE_FETCH_CART_FAILURE:
      return false;
    default:
      return state;
  }
};

const isFetched = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_FETCH_CART_REQUEST:
    case ActionTypes.HANDLE_FETCH_CART_FAILURE:
      return false;
    case ActionTypes.HANDLE_FETCH_CART_SUCCESS:
      return true;
    default:
      return state;
  }
};

const isShowCartModal = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_TOGGLE_CART_MODAL: {
      return !state;
    }
    default:
      return state;
  }
};

const isValidatingCart = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_SUBMIT_CART_REQUEST:
    case ActionTypes.HANDLE_CHECKOUT_CART_REQUEST:
      return true;
    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
    case ActionTypes.HANDLE_SUBMIT_CART_FAILURE:
    case ActionTypes.HANDLE_SUBMIT_BUYER_DETAILS_SUCCESS:
    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS:
    case ActionTypes.HANDLE_CHECKOUT_CART_FAILURE:
      return false;
    default:
      return state;
  }
};

const isCartFinalized = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_SUBMIT_CART_REQUEST:
    case ActionTypes.HANDLE_SUBMIT_CART_FAILURE:
    case ActionTypes.HANDLE_ADD_TO_CART_SUCCESS:
    case ActionTypes.HANDLE_REMOVE_ITEM_FROM_CART_SUCCESS:
    case ActionTypes.HANDLE_EDIT_CART_ITEM_QUANTITY_SUCCESS:
      return false;
    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
      return true;
    default:
      return state;
  }
};

const isShippingFinalized = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_SUBMIT_CART_REQUEST:
    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
    case ActionTypes.HANDLE_SUBMIT_CART_FAILURE:
    case ActionTypes.HANDLE_ON_CHANGE_SHIPPING:
      return false;
    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS:
      return true;
    default:
      return state;
  }
};

const sellerUsername = root.location.pathname.split('/').filter(path => path)[0];
const settings = (state = {
  seller: {
    username: sellerUsername,
  },
}, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS: {
      return {
        ...state,
        showConvenienceFee: action.response.details.showConvenienceFeeAlert,
      };
    }

    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS: {
      return {
        ...state,
        isInternationalNumberEnabled: action.response.isInternationalEnabled,
      };
    }

    case PaymentSettingsActionTypes.HANDLE_AWAITING_PAYMENT_COMPLETION: {
      return {
        ...state,
        isAwaitingPaymentCompletion: action.waitingStatus,
      };
    }

    case ActionTypes.HANDLE_TOGGLE_CART_MODAL: {
      return {
        ...state,
        isAwaitingPaymentCompletion: false,
      };
    }

    default:
      return state;
  }
};

const { TABS } = ActionTypes;
const openTab = (state = TABS.CART, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_FETCH_CART_SUCCESS:
      return TABS.CART;

    case ActionTypes.HANDLE_TOGGLE_CART_OPEN_TAB:
      return action.payload.tab;

    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
      return TABS.BUYER_DETAILS;

    case ActionTypes.HANDLE_SUBMIT_BUYER_DETAILS_SUCCESS:
      return TABS.SHIPPING;

    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS:
      if (Number(action.response.details.totalAmount)) {
        return TABS.PAYMENT;
      }
      return state;

    default:
      return state;
  }
};

const orderOfTabs = [TABS.CART, TABS.BUYER_DETAILS, TABS.SHIPPING, TABS.PAYMENT];
const indexOf = tab => orderOfTabs.indexOf(tab);

const isBackwardMotion = (state = false, action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_TOGGLE_CART_OPEN_TAB:
      const { tab: nextTab, currentTab } = action.payload;
      if (indexOf(nextTab) < indexOf(currentTab)) {
        return true;
      }
      return false;

    case ActionTypes.HANDLE_SUBMIT_CART_SUCCESS:
    case ActionTypes.HANDLE_CHECKOUT_CART_SUCCESS:
      return false;

    default:
      return state;
  }
};


const globalError = (state = '', action) => {
  switch (action.type) {
    case ActionTypes.HANDLE_CART_GLOBAL_ERROR:
      const { error } = action.payload;
      return error;
    case ActionTypes.HANDLE_SUBMIT_CART_FAILURE:
      try {
        const error = action.errors.errors.All_;
        if (error) {
          return error;
        } else {
          return '';
        }
      } catch (e) {
        return '';
      }
    case ActionTypes.HANDLE_REMOVE_GLOBAL_CART_ERROR:
      return '';
    default:
      return state;
  }
};


export default combineReducers({
  items,
  outOfStockItems,
  isProcessingCart,
  shipping,
  finalizePurchase,
  settings,
  otherCartProps,
  isFetching,
  isFetched,
  isShowCartModal,
  isValidatingCart,
  isCartFinalized,
  isShippingFinalized,
  openTab,
  isBackwardMotion,
  globalError,
});

export const getCartItems = state => fromItems.getCartItems(state.items);
export const getOutOfStockCartItems = state => fromOutOfStockItems.getCartItems(state.outOfStockItems);
export const getIsProcessingCart = state => state.isProcessingCart;
export const getCartAmount = state => state.otherCartProps.amount;
export const getIsFetchingCart = state => state.isFetching;
export const getCartItemsCount = state => state.items.length;
export const getIsCartFetched = state => state.isFetched;
export const getIsShowCartModal = state => state.isShowCartModal;
export const getIsShippingRequired = state => state.otherCartProps.isShippingRequired;
export const getShippingCharges = state => state.otherCartProps.shippingCharges;
export const getIsValidatingCart = state => state.isValidatingCart;
export const getIsCartFinalized = state => state.isCartFinalized;
export const getIsCartPayable = state => state.isCartFinalized && state.isShippingFinalized;
export const getShippingDetails = state => fromShipping.getShippingDetails(state.shipping);
export const getOpenTab = state => state.openTab;
export const getIsBackwardMotion = state => state.isBackwardMotion;
export const getClaimUrl = state => state.otherCartProps.claimUrl;
export const getSellerUsername = state => state.settings.seller.username;
export const getIsInternationalNumberEnabled = state => state.settings.isInternationalNumberEnabled;
export const getGlobalError = state => state.globalError;
export const getIsAwaitingPaymentCompletion = state => state.settings.isAwaitingPaymentCompletion;



// WEBPACK FOOTER //
// ./src/cart/reducers/CartReducers.js